/**
 * ClassPlanningService
 * Serviço de Planejamento Inteligente de Aulas.
 *
 * Módulos internos:
 *   engine.js  — algoritmos puros (sem Firebase): calculateObjectiveAverages, generateWeeklyCycle, distributePlanToSessions, needsRegeneration
 *   storage.js — operações Firestore: savePlan, saveCycle, getFutureSessions
 */
import {
    calculateObjectiveAverages,
    generateWeeklyCycle,
    distributePlanToSessions,
    needsRegeneration,
    getWeekKey,
} from "./engine";
import { savePlan, saveCycle, getFutureSessions } from "./storage";

/**
 * Orquestra toda a geração do plano para um grupo de turmas.
 * deps = { ActivityService, LevelService, EnrollmentService, EvaluationService, GamificationService }
 */
const generatePlanForGroup = async (idTenant, idActivity, classGroupId, classIds, currentEventId, deps) => {
    const { ActivityService, LevelService, EnrollmentService, EvaluationService, GamificationService } = deps;

    let masteryThresholdPercent = 70;
    if (GamificationService) {
        try {
            const gamificationSettings = await GamificationService.getSettings(idTenant);
            masteryThresholdPercent = gamificationSettings?.minScoreToPassPercent ?? 70;
        } catch (e) {
            console.warn("[generatePlanForGroup] Erro ao buscar gamification settings, usando 70% padrão", e);
        }
    }

    const allActivities = await ActivityService.getActivities(idTenant);
    const activity = allActivities.find(a => a.id === idActivity);
    if (!activity) throw new Error("Atividade não encontrada.");

    const objectives = (activity.objectives || []).map(obj => ({
        id: obj.id,
        name: obj.name,
        topics: (obj.topics || []).map(t => ({
            id: t.id,
            name: t.name,
            isFundamental: !!t.isFundamental
        }))
    }));

    if (objectives.length === 0) throw new Error("Atividade sem objetivos configurados.");

    const levels = await LevelService.getLevels(idTenant);
    const maxLevel = levels.reduce((max, l) => (Number(l.order || 0) > Number(max.order || 0) ? l : max), levels[0] || { order: 0 });
    const maxLevelValue = Number(maxLevel.order || 0);

    const memberIdsSet = new Set();
    for (const classId of classIds) {
        const enrollments = await EnrollmentService.getEnrolledMembersByClass(idTenant, classId);
        enrollments.forEach(e => { if (!memberIdsSet.has(e.id)) memberIdsSet.add(e.id); });
    }

    const idMembers = Array.from(memberIdsSet);
    let evaluationsMap = {};
    if (idMembers.length > 0) {
        evaluationsMap = await EvaluationService.getLatestByMemberAndActivity(idTenant, idActivity, idMembers);
    }
    const evaluations = Object.values(evaluationsMap);

    const objectiveAverages = calculateObjectiveAverages(evaluations, objectives, levels);

    const avgValues = Object.values(objectiveAverages).map(o => o.avg);
    const classAvgOverall = avgValues.length > 0
        ? avgValues.reduce((a, b) => a + b, 0) / avgValues.length
        : 0;

    const masteryThreshold = maxLevelValue * (masteryThresholdPercent / 100);
    const cycle = generateWeeklyCycle(objectiveAverages, maxLevelValue, masteryThreshold, 2);

    const allSessions = await getFutureSessions(idTenant, classIds);
    if (allSessions.length === 0) return { sessionsUpdated: 0, cycle, objectiveAverages };

    const sessionsWithPlan = distributePlanToSessions(allSessions, cycle, currentEventId, idMembers.length, classAvgOverall);
    const sessionsUpdated = await savePlan(idTenant, sessionsWithPlan);

    const firstWeekKey = sessionsWithPlan.length > 0
        ? getWeekKey(sessionsWithPlan[0].sessionDate)
        : getWeekKey(new Date().toISOString().slice(0, 10));

    const firstSession = allSessions[0] || {};
    await saveCycle(idTenant, classGroupId, cycle, {
        anchorWeekKey: firstWeekKey,
        evaluationEventId: currentEventId,
        classMemberCount: idMembers.length,
        classAvgOverall,
        idEmployee: firstSession.idEmployee || "",
        employeeName: firstSession.employeeName || "",
        employeePhone: firstSession.employeePhone || "",
        employeeEmail: firstSession.employeeEmail || "",
        employeePhotoUrl: firstSession.employeePhotoUrl || "",
    });

    return { sessionsUpdated, cycle, objectiveAverages };
};

const ClassPlanningService = {
    calculateObjectiveAverages,
    generateWeeklyCycle,
    distributePlanToSessions,
    needsRegeneration,
    savePlan,
    saveCycle,
    generatePlanForGroup,
};

export default ClassPlanningService;
